import { MouseEvent, ReactNode, useEffect, useRef } from 'react'
import { isNativeApp } from '../../../client'
import { useGridBlock, usePage } from '../../../contexts'
import { storiesHe } from '../../../locales/stories.he'
import { sendGA4 } from '../../../utils'
import { useModalDialog } from '../Reels/focusTrap'
import {
  CheckIcon,
  ChevronIcon,
  CloseIcon,
  Logo13Icon,
  SkipIcon,
} from './icons'
import { STORIES_IMAGES } from './storiesImages'
import { StoriesLoadingFrame } from './StoriesLoadingFrame'
import {
  ChevronButton,
  RingButton,
  Side,
  CloseButton as ViewerClose,
  Shade,
} from './StoriesOverlay.style'
import {
  AdBlockGroup,
  AdBlockGroups,
  AdBlockHand,
  AdBlockIntro,
  AdBlockLogo,
  AdBlockScroll,
  AdBlockStage,
  AdBlockText,
  GeoLogo,
  GeoMark,
  GeoStage,
  GeoText,
  LoadingPoster,
  ScreenBackdrop,
  ScreenClose,
  ScreenFrame,
  ScreenStage,
} from './StoriesScreens.style'

/** the page behind does not scroll, as behind the viewer */
const useLockPage = () =>
  useEffect(() => {
    const { style } = document.body
    const previousOverflow = style.overflow
    style.overflow = 'hidden'
    return () => {
      style.overflow = previousOverflow
    }
  }, [])

interface IScreenProps {
  onClose: () => void
}

/** the viewer's window as a modal dialog: Escape and a click outside close it */
const Screen = ({
  onClose,
  children,
}: IScreenProps & { children: ReactNode }) => {
  const ref = useRef<HTMLDivElement>(null)
  const onKeyDown = useModalDialog(ref, onClose)
  useLockPage()
  const onBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return (
    <ScreenBackdrop
      ref={ref}
      role='dialog'
      aria-modal='true'
      aria-label={storiesHe.dialog}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      onClick={onBackdropClick}
    >
      {children}
    </ScreenBackdrop>
  )
}

const CloseButton = ({ onClose }: IScreenProps) => (
  <ScreenClose type='button' aria-label={storiesHe.close} onClick={onClose}>
    <CloseIcon />
  </ScreenClose>
)

/**
 * Desktop: the viewer's side buttons where the viewer has them, dimmed to 40%
 * and inactive, as "D_Ad Blocker" (24339:376955) and "D_Geo Block"
 * (24497:405712) draw them: on these screens there is nowhere to go
 */
const DimmedSides = () => (
  <>
    <Side $side='left' $disabled aria-hidden='true'>
      <RingButton type='button' $flip disabled tabIndex={-1}>
        <SkipIcon />
      </RingButton>
      <ChevronButton type='button' disabled tabIndex={-1}>
        <ChevronIcon direction='left' />
      </ChevronButton>
    </Side>
    <Side $side='right' $disabled aria-hidden='true'>
      <ChevronButton type='button' disabled tabIndex={-1}>
        <ChevronIcon direction='right' />
      </ChevronButton>
      <RingButton type='button' disabled tabIndex={-1}>
        <SkipIcon />
      </RingButton>
    </Side>
  </>
)

interface ILoadingScreenProps extends IScreenProps {
  /** the frame the clip opens on: the url the viewer paints, so it comes from the cache */
  poster: string
  /** when the user tapped (Date.now()); the viewer counts its own wait from there too */
  since: number
}

/**
 * Figma "M_Video Play_Loader" (24339:374895) and "D_Video Play_Loader"
 * (24339:376853), while the viewer's code is still on its way: the clip's
 * frame and its shade under the loading frame, and the viewer's close button
 * above it. The viewer takes over the same frame where this one stands.
 */
export const StoriesLoadingScreen = ({
  onClose,
  poster,
  since,
}: ILoadingScreenProps) => (
  <Screen onClose={onClose}>
    <ScreenStage>
      <LoadingPoster
        style={poster ? { backgroundImage: `url("${poster}")` } : undefined}
      />
      <Shade />
      <StoriesLoadingFrame since={since} label={storiesHe.loading} />
      <ViewerClose type='button' aria-label={storiesHe.close} onClick={onClose}>
        <CloseIcon />
      </ViewerClose>
    </ScreenStage>
  </Screen>
)

/**
 * Figma "M_Ad blocker" (24237:374777), shown instead of the viewer when an ad
 * blocker is found (D27). It sends the same GA4 event as the reels' screen.
 */
export const StoriesAdBlock = ({ onClose }: IScreenProps) => {
  const pageData = usePage()
  const { gridId } = useGridBlock()
  const text = storiesHe.adBlock

  useEffect(() => {
    sendGA4({
      eventName: isNativeApp() ? 'app_verticaladblock' : 'verticaladblock',
      pageData,
      Component_Type: 'shorts',
      component_id: gridId,
    })
    // once, when the screen is shown
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Screen onClose={onClose}>
      <ScreenFrame>
        <DimmedSides />
        <AdBlockStage>
          <AdBlockHand src={STORIES_IMAGES.adBlockHand} alt='' />
          <CloseButton onClose={onClose} />
          <AdBlockScroll>
            <AdBlockText>
              <AdBlockIntro>
                <h2>{text.title}</h2>
                <p>{text.intro}</p>
              </AdBlockIntro>
              <AdBlockGroups>
                <AdBlockGroup>
                  <h3>{text.causesTitle}</h3>
                  <ul>
                    {text.causes.map(cause => (
                      <li key={cause.lead}>
                        <img src={STORIES_IMAGES.adBlockHandMark} alt='' />
                        <span>
                          <b>{cause.lead}</b>
                          {cause.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </AdBlockGroup>
                <AdBlockGroup>
                  <h3>{text.fixTitle}</h3>
                  <ul>
                    {text.fixes.map(fix => (
                      <li key={fix}>
                        <CheckIcon />
                        <span>{fix}</span>
                      </li>
                    ))}
                  </ul>
                </AdBlockGroup>
              </AdBlockGroups>
            </AdBlockText>
            <AdBlockLogo>
              <Logo13Icon />
            </AdBlockLogo>
          </AdBlockScroll>
        </AdBlockStage>
      </ScreenFrame>
    </Screen>
  )
}

/**
 * Figma "M_Geo Block" (24497:410939) / "D_Geo Block" (24497:405712), instead
 * of the viewer outside Israel, where the clips do not play (D45)
 */
export const StoriesGeoBlock = ({ onClose }: IScreenProps) => (
  <Screen onClose={onClose}>
    <ScreenFrame>
      <DimmedSides />
      <GeoStage>
        <GeoMark src={STORIES_IMAGES.geoMark} alt='' />
        <CloseButton onClose={onClose} />
        <GeoText>
          {storiesHe.geoBlock[0]}
          <br />
          {storiesHe.geoBlock[1]}
        </GeoText>
        <GeoLogo src={STORIES_IMAGES.geoLogo} alt='' />
      </GeoStage>
    </ScreenFrame>
  </Screen>
)
